import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { S_POS, B_POS } from "@/hooks/useGame";
import Avatar from "@/components/scene/Avatar";
import SoundRings from "@/components/scene/SoundRings";
import { FireParticles } from "@/components/scene/Hazards";
import ToonNPC, { NPC_PALETTE } from "@/components/scene/ToonNPC";
import RoofDivergence from "@/components/scene/RoofDivergence";
import PhotoPlane from "@/components/scene/PhotoPlane";
import VolumetricSmoke from "@/components/scene/VolumetricSmoke";

const C = {
  gold: 0xc9a24b,
  ring: 0xf2c85b,
  flash: 0xffe9a8,
  wall: 0x8a8578,
  debris: 0x3a3d42,
  npc: 0x6b6f77,
  npcActive: 0x8b7a44,
};

function BlockyNPC({ position = [0, 0, 0], color = C.npc, lying = false, rotation = [0, 0, 0] }) {
  const mat = useMemo(
    () => new THREE.MeshStandardMaterial({ color, roughness: 0.9, metalness: 0.05 }),
    [color]
  );
  return (
    <group position={position} rotation={lying ? [Math.PI / 2, 0, rotation[2]] : rotation}>
      <mesh material={mat} position={[0, 0.55, 0]}>
        <boxGeometry args={[0.36, 0.7, 0.24]} />
      </mesh>
      <mesh material={mat} position={[0, 1.06, 0]}>
        <boxGeometry args={[0.26, 0.26, 0.26]} />
      </mesh>
      <mesh material={mat} position={[-0.26, 0.6, 0]}>
        <boxGeometry args={[0.12, 0.5, 0.12]} />
      </mesh>
      <mesh material={mat} position={[0.26, 0.6, 0]}>
        <boxGeometry args={[0.12, 0.5, 0.12]} />
      </mesh>
    </group>
  );
}

export default function AcousticDiorama({ game, audio }) {
  const flashPivot = useRef();
  const coneMat = useRef();
  const spot = useRef();
  const spotTarget = useRef();
  const breachMat = useRef();
  const wallMat = useRef();
  const stanleyRef = useRef();
  const brianRef = useRef();
  const stanleyArm = useRef();

  useEffect(() => {
    if (spot.current && spotTarget.current) spot.current.target = spotTarget.current;
  }, []);

  const debris = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      arr.push({
        s: [0.3 + Math.random() * 0.5, 0.12 + Math.random() * 0.3, 0.3 + Math.random() * 0.4],
        p: [S_POS.x + (Math.random() - 0.5) * 1.8, 0.1 + Math.random() * 0.4, (Math.random() - 0.5) * 2.4],
        r: [Math.random(), Math.random(), Math.random()],
      });
    }
    return arr;
  }, []);

  useFrame((state, dt) => {
    const g = game.current;
    const t = state.clock.elapsedTime;

    // flashlight alignment
    if (g.autoAligning) g.sweep += (g.target - g.sweep) * Math.min(1, dt * 2.2);
    if (flashPivot.current) flashPivot.current.rotation.z = g.sweep;
    const err = Math.abs(g.sweep - g.target);
    const nowAligned = err < 0.09;
    g.aligned = nowAligned;
    const glow = nowAligned ? 0.42 : THREE.MathUtils.clamp(0.16 + (0.2 - err) * 0.6, 0.1, 0.32);
    if (coneMat.current) coneMat.current.opacity = glow;
    if (spot.current) spot.current.intensity = nowAligned ? 3.2 : THREE.MathUtils.clamp((0.25 - err) * 6, 0, 2);
    if (breachMat.current) breachMat.current.opacity = nowAligned ? 0.9 : 0.0;
    if (nowAligned && !g.firedAligned && (g.phase === "flashlight" || g.phase === "acoustic")) {
      g.firedAligned = true;
      audio && audio.alignChime();
      g.onAligned && g.onAligned();
    }

    // wall opacity: base (ghosted in 3rd-person / x-ray) minus pull dissolve
    const base = g.wallOpacity != null ? g.wallOpacity : 0.5;
    const p = g.pullProgress;
    const ease0 = p > 0 ? p * p * (3 - 2 * p) : 0;
    if (wallMat.current) wallMat.current.opacity = Math.max(0, base - ease0 * 0.3);

    // Brian's POV: hide his own body while the camera is at/near his spot (first-person, incl. D-pad
    // offsets of up to ~3.7). Universal orbit sits further out, and other POVs always show him.
    if (brianRef.current) {
      const c = state.camera.position;
      const dx = c.x - B_POS.x, dz = c.z;
      brianRef.current.visible = !(g.pov === "brian" && dx * dx + dz * dz < 4.0 * 4.0);
    }

    // wrist-lock pull
    if (p > 0 && stanleyRef.current) {
      const ease = ease0;
      stanleyRef.current.position.x = S_POS.x + (1.35 - S_POS.x) * ease;
      stanleyRef.current.position.y = Math.sin(ease * Math.PI) * 0.9;
      if (stanleyArm.current) stanleyArm.current.rotation.z = Math.PI / 2 - ease * 0.6;
    }
  });

  return (
    <group>
      <fogExp2 attach="fog" args={[0x141518, 0.1]} />
      <ambientLight color={0x2a2f36} intensity={0.7} />
      <directionalLight color={0xbfa76a} intensity={0.35} position={[3, 8, 6]} />
      <pointLight color={C.gold} intensity={1.4} distance={9} position={[S_POS.x - 0.4, S_POS.y + 0.6, 1.6]} />
      <pointLight color={0x9fb4c8} intensity={1.0} distance={9} position={[B_POS.x + 0.4, B_POS.y + 0.5, 1.6]} />

      {/* floor + grid */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[40, 26]} />
        <meshStandardMaterial color={0x101216} roughness={0.6} metalness={0.3} />
      </mesh>
      <gridHelper args={[40, 40, 0x24272b, 0x1a1d21]} />

      {/* partition wall + edges + breach */}
      <mesh position={[0, 1.7, 0]}>
        <boxGeometry args={[0.18, 3.4, 4]} />
        <meshStandardMaterial ref={wallMat} color={C.wall} roughness={0.95} transparent opacity={0.5} />
      </mesh>
      <lineSegments position={[0, 1.7, 0]}>
        <edgesGeometry args={[new THREE.BoxGeometry(0.18, 3.4, 4)]} />
        <lineBasicMaterial color={0xb9b29e} transparent opacity={0.5} />
      </lineSegments>
      <mesh position={[0, S_POS.y + 0.1, 0]}>
        <torusGeometry args={[0.34, 0.05, 12, 28]} />
        <meshBasicMaterial ref={breachMat} color={C.ring} transparent opacity={0} />
      </mesh>

      {/* debris */}
      {debris.map((d, i) => (
        <mesh key={i} position={d.p} rotation={d.r}>
          <boxGeometry args={d.s} />
          <meshStandardMaterial color={C.debris} roughness={1} />
        </mesh>
      ))}

      {/* archival devastation backdrop (post-impact 81st floor) */}
      <PhotoPlane url="/archival/devastated_office.png" args={[9, 7]} position={[S_POS.x - 2.2, 2.6, -3.2]} rotation={[0, 0.5, 0]} opacity={0.9} emissive tone={false} />
      <PhotoPlane url="/archival/dark_lobby.png" args={[10, 5.6]} position={[4.5, 2.4, -3.4]} rotation={[0, -0.45, 0]} opacity={0.75} emissive tone={false} />

      {/* toon-shaded casualties (respectful, museum-distance) */}
      <ToonNPC position={[S_POS.x - 1.7, 0, 1.7]} pose="lie" rotation={[0, 0.4, 0]} shirt={0x5a5f66} pants={0x3a3d42} />
      <ToonNPC position={[S_POS.x + 0.5, 0, -1.9]} pose="lie" rotation={[0, -0.3, 0]} shirt={0x6b6f77} pants={0x33363c} />

      {/* the group that diverged upward toward the locked roof door */}
      <RoofDivergence position={[-6.4, 0, -2.4]} rotation={[0, 0.55, 0]} />

      {/* distant fire hazard (obscuring, motivates flashlight) */}
      <FireParticles position={[-5.5, 0, -3]} count={70} scale={1.1} intensity={0.9} />
      <FireParticles position={[5.5, 0, -3.5]} count={50} scale={0.9} intensity={0.7} />

      {/* sound rings + origin pulse */}
      <SoundRings game={game} />

      {/* smoke fills Stanley's side of the 81st floor; follows the SMOKE dial */}
      <VolumetricSmoke count={22} area={[7, 3.5, 6]} center={[S_POS.x - 1.2, 2.2, 0]} color={0x201d17} opacity={0.6} rise={0.3} getIntensity={() => Math.min(1, 0.5 + 0.7 * (game.current.smokeDensity ?? 0.7))} clearPoint={[S_POS.x + 0.2, 1.5, 0.5]} clearRadius={1.4} />

      {/* avatars */}
      {/* Stanley is represented by smoke here (no rigged model), in every view. The group still
          follows the wrist-lock pull, so the smoke drifts toward the breach as he is pulled out.
          clearPoint keeps Stanley's own first-person camera (S_POS.x + 0.2, 1.5, 0.5) from being blanked. */}
      <group ref={stanleyRef} position={[S_POS.x, 0, 0]}>
        <VolumetricSmoke count={10} area={[1.6, 1.8, 1.6]} center={[0, 1.1, 0]} color={0x24211b} opacity={0.75} rise={0.22} getIntensity={() => 0.9} clearPoint={[0.2, 1.5, 0.5]} clearRadius={1.2} />
      </group>
      <group ref={brianRef} position={[B_POS.x, 0, 0]}>
        <Avatar which="brian" url="/models/brian.glb" faceSign={1} getMotion={() => "idle"} getCough={() => game.current.coughLevel || 0} rig={{ base: "/models/brian_rig.glb", run: "/models/brian_run.glb" }} getRunning={() => game.current.running} />
      </group>

      {/* flashlight pivot from Brian */}
      <group ref={flashPivot} position={[B_POS.x, B_POS.y, B_POS.z]}>
        <mesh rotation={[0, 0, Math.PI / 2]} position={[-2.2, 0, 0]}>
          <coneGeometry args={[1.05, 4.4, 30, 1, true]} />
          <meshBasicMaterial
            ref={coneMat}
            color={C.flash}
            transparent
            opacity={0.16}
            side={THREE.DoubleSide}
            depthWrite={false}
            blending={THREE.AdditiveBlending}
          />
        </mesh>
        <spotLight
          ref={spot}
          color={C.flash}
          intensity={0}
          distance={12}
          angle={Math.PI / 7}
          penumbra={0.5}
          decay={1}
        />
        <object3D ref={spotTarget} position={[-4, 0, 0]} />
      </group>
    </group>
  );
}
